import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { usePreferencesStore, type Locale } from '@/store/preferencesStore';

export type UserRole = 'employer' | 'employee' | 'admin';

export interface CurrentUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: UserRole[];
  /** Whether the user's email has been verified */
  emailVerified?: boolean;
  /** The account's preferred UI language (applied on login). */
  language?: Locale;
}

interface AuthState {
  user: CurrentUser | null;
  token: string | null;
  /** Primary role derived from user.roles[0] */
  role: UserRole | null;

  login: (user: CurrentUser, token: string) => void;
  logout: () => void;
  updateUser: (partial: Partial<CurrentUser>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      role: null,

      login(user, token) {
        set({
          user,
          token,
          role: user.roles[0] ?? null,
        });
        // Enter the app in the account's preferred language — this is what carries the
        // choice across devices/browsers, where localStorage wouldn't have it.
        if (user.language) {
          usePreferencesStore.getState().setLocale(user.language);
        }
      },

      logout() {
        set({ user: null, token: null, role: null });
      },

      updateUser(partial) {
        const current = get().user;
        if (!current) return;
        const updated = { ...current, ...partial };
        set({ user: updated, role: updated.roles[0] ?? null });
      },
    }),
    {
      name: 'casalary-auth',
      // Only persist token and basic user data — not the full object
      partialize: (state) => ({
        user: state.user
          ? {
              _id: state.user._id,
              firstName: state.user.firstName,
              lastName: state.user.lastName,
              email: state.user.email,
              roles: state.user.roles,
              emailVerified: state.user.emailVerified,
              language: state.user.language,
            }
          : null,
        token: state.token,
        role: state.role,
      }),
    }
  )
);
